import { Typography } from '@mui/material'
import type { Step } from 'react-joyride'
import { Link } from 'react-router-dom'
import type { Breakpoint } from '~hooks/use-breakpoint'
import { lastStep } from './common-steps'

interface OperationsTab {
  name: string
  path: string
}

const tabSteps: Record<string, Step> = {
  'partner-dashboard': {
    target: '#current-voyage-table',
    placement: 'top',
    content: (
      <>
        <Typography variant="label" sx={{ mb: 3 }} component="p">
          Vessel dashboard
        </Typography>
        <Typography variant="paragraph2" component="p">
          The Vessel dashboard gives you a current operational overview of the
          vessel. The page highlights the last three cargoes, the status of
          vessel surveys, possible rejections and other high-level facts about
          the vessel.
        </Typography>
        <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
          Specifically, this is where you can easily find details on the
          vessel’s current voyage.
        </Typography>
      </>
    ),
  },
  'basic-vessel-data': {
    target: 'body',
    content: (
      <>
        <Typography variant="label" sx={{ mb: 3 }} component="p">
          Basic Vessel Data
        </Typography>
        <Typography variant="paragraph2" component="p">
          Vessel specifications are imported from Q88, with possible overwrites
          from Maersk Tankers. A version history of the values is available, and
          you can see who is responsible for each input.
        </Typography>
      </>
    ),
  },
  documents: {
    target: 'body',
    content: (
      <>
        <Typography variant="label" sx={{ mb: 3 }} component="p">
          Certificates
        </Typography>
        <Typography variant="paragraph2" component="p">
          Find the vessel’s certificates and documents, including date of issue,
          expiry dates and upcoming surveys. Certificates may be uploaded by
          Masters, your operations department or someone from Maersk Tankers.
        </Typography>
        <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
          To learn more, take the Certificates tour from the{' '}
          <Link to="/help">Help page</Link>.
        </Typography>
      </>
    ),
  },
  questionnaire: {
    target: 'body',
    content: (
      <>
        <Typography variant="label" sx={{ mb: 3 }} component="p">
          Questionnaire
        </Typography>
        <Typography variant="paragraph2" component="p">
          Review the vessel onboarding questionnaire, including the answers and
          any comments added to them.
        </Typography>
      </>
    ),
  },
  vetting: {
    target: 'body',
    content: (
      <>
        <Typography variant="label" sx={{ mb: 3 }} component="p">
          Vetting
        </Typography>
        <Typography variant="paragraph2" component="p">
          Review the vessel’s vetting history, including SIRE and CDI
          inspections, rejections and incidents.
        </Typography>
        <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
          You can also find the vetting overview for all of your vessels in the
          Operations tab in the main menu.
        </Typography>
      </>
    ),
  },
  voyages: {
    target: 'body',
    content: (
      <>
        <Typography variant="label" sx={{ mb: 3 }} component="p">
          Voyages
        </Typography>
        <Typography variant="paragraph2" component="p">
          Explore the vessel’s past, present and potentially future voyages
          here, with the voyage information you may recognise from another VMS
          system.
        </Typography>
      </>
    ),
  },
}

export function constructFleetTour({
  breakpoint,
  vessel,
  operationsTabs: availableOperationsTabs,
}: {
  breakpoint: Breakpoint
  vessel?: { vesselId: string; vesselName: string }
  operationsTabs: OperationsTab[]
}): Step[] {
  const isSmallSizeTour = ['xs', 'sm', 'md'].includes(breakpoint)
  const operationsTabs = availableOperationsTabs.filter(
    ({ path }) => tabSteps[path],
  )
  const vesselPrefix = vessel
    ? `/fleet/vessels/${encodeURIComponent(vessel.vesselId)}`
    : undefined
  const fleetMenuLink = '/fleet/vessels'
  const fleetMenuTarget = `.tour-step-nav-pane a[href="${fleetMenuLink}"]`
  // The mobile menu is closed until this step opens it, so fall back to the
  // vessel list to tell that the page is ready and the tour can start.
  const mobileVesselTarget = () =>
    document.querySelector<HTMLElement>(fleetMenuTarget) ??
    document.querySelector<HTMLElement>('table tbody tr')
  const vesselTarget = isSmallSizeTour
    ? mobileVesselTarget
    : vessel
      ? () =>
          document.querySelector<HTMLElement>(
            `[data-tour-vessel-id="${encodeURIComponent(vessel.vesselId)}"]`,
          ) ??
          (document.querySelector('table tbody tr')
            ? document.querySelector<HTMLElement>(fleetMenuTarget)
            : null)
      : 'body'
  const firstTabLink =
    vesselPrefix && operationsTabs[0]
      ? `${vesselPrefix}/${operationsTabs[0].path}`
      : undefined

  const steps: Step[] = [
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Welcome to Fleet
          </Typography>
          <Typography variant="paragraph2" component="p">
            Fleet is our operational hub for collaboration and sharing
            information particular to your vessel. You have access to your own
            vessels and their details.
          </Typography>
          <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
            You can skip the tour at any point, and you can always start it
            again from the <Link to="/help">Help page</Link>.
          </Typography>
        </>
      ),
      target: 'body',
      placement: 'center',
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            {isSmallSizeTour ? 'Fleet' : 'Vessels'}
          </Typography>
          <Typography variant="paragraph2" component="p">
            Find your vessels here. If you have more than one, they will appear
            in the {isSmallSizeTour ? 'vesel list' : 'list below'}. Select any
            vessel to open its details.
            {vessel && ` The tour uses ${vessel.vesselName} as an example.`}
          </Typography>
          {operationsTabs.length > 0 && (
            <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
              Next, we’ll visit each vessel details tab for this vessel.
            </Typography>
          )}
        </>
      ),
      target: vesselTarget,
      placement: isSmallSizeTour ? 'bottom' : vessel ? 'right' : 'center',
      data: {
        ...(isSmallSizeTour && { navigationLink: fleetMenuLink }),
        ...(firstTabLink && { next: firstTabLink, vesselLink: firstTabLink }),
      },
    },
    ...operationsTabs.map<Step>(({ path }, index) => {
      const tabLink = `${vesselPrefix}/${path}`
      // On mobile every tab step points at its menu item, even tabs that
      // highlight something on the page on desktop.
      const pointsAtMenu = isSmallSizeTour || tabSteps[path].target === 'body'
      return {
        ...tabSteps[path],
        ...(pointsAtMenu && {
          target: `.tour-step-nav-pane a[href="${tabLink}"]`,
          placement: isSmallSizeTour ? 'bottom' : 'right',
        }),
        data: {
          navigationLink: pointsAtMenu ? tabLink : undefined,
          ...(index > 0
            ? { previous: `${vesselPrefix}/${operationsTabs[index - 1].path}` }
            : { previous: fleetMenuLink }),
          ...(index < operationsTabs.length - 1
            ? { next: `${vesselPrefix}/${operationsTabs[index + 1].path}` }
            : {}),
        },
      }
    }),
    ...(!vessel || operationsTabs.length === 0
      ? [
          {
            content: (
              <>
                <Typography variant="label" sx={{ mb: 3 }} component="p">
                  Vessel details tabs
                </Typography>
                <Typography variant="paragraph2" component="p">
                  Open a vessel to see the vessel details tabs available to you.
                  The tabs depend on your access.
                </Typography>
              </>
            ),
            target: 'body',
            placement: 'center' as const,
          },
        ]
      : []),
    lastStep,
  ]

  return steps.map((step) => ({
    ...step,
    skipBeacon: true,
    targetWaitTimeout: 15000,
    data: { ...step.data, deferredTarget: true },
  }))
}
